/**
 * Ad Impression Tracking using Intersection Observer
 * Tracks impressions when ads actually come into viewport (scroll-based)
 */

(function() {
    'use strict';

    // Track which ads have already been tracked in this session to avoid duplicate impressions
    const trackedAds = new Set();
    
    // localStorage key for storing ad impression timestamps
    const STORAGE_KEY = 'ad_impressions_tracking';
    // 24 hours in milliseconds
    const ONE_DAY_MS = 24 * 60 * 60 * 1000;

    function hasTargetingConsent() {
        const rawGroups = (window.OnetrustActiveGroups || '').toString();
        if (!rawGroups) {
            return false;
        }
        const groups = rawGroups.split(',').map(function(group) {
            return group.trim();
        });
        return groups.indexOf('C0004') !== -1;
    }

    /**
     * Get stored ad impressions from localStorage
     * @returns {Object} Object with ad IDs as keys and timestamps as values
     */
    function getStoredImpressions() {
        if (!hasTargetingConsent()) {
            return {};
        }
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            // console.warn('Error reading ad impressions from localStorage:', e);
        }
        return {};
    }

    /**
     * Save ad impression timestamp to localStorage
     * @param {string} adId Ad ID
     */
    function saveImpression(adId) {
        if (!hasTargetingConsent()) {
            return;
        }
        try {
            const impressions = getStoredImpressions();
            impressions[adId] = Date.now();
            localStorage.setItem(STORAGE_KEY, JSON.stringify(impressions));
            
            // Clean up old entries (older than 7 days) to prevent localStorage from growing
            const sevenDaysAgo = Date.now() - (7 * ONE_DAY_MS);
            Object.keys(impressions).forEach(function(key) {
                if (impressions[key] < sevenDaysAgo) {
                    delete impressions[key];
                }
            });
            localStorage.setItem(STORAGE_KEY, JSON.stringify(impressions));
        } catch (e) {
            // console.warn('Error saving ad impression to localStorage:', e);
        }
    }

    /**
     * Check if an ad can be tracked (hasn't been tracked in the last 24 hours)
     * @param {string} adId Ad ID
     * @returns {boolean} True if ad can be tracked, false otherwise
     */
    function canTrackAd(adId) {
        const impressions = getStoredImpressions();
        const lastTracked = impressions[adId];
        
        if (!lastTracked) {
            // Never tracked before, can track
            return true;
        }
        
        const now = Date.now();
        const timeSinceLastTrack = now - lastTracked;
        
        // Can track if more than 24 hours have passed
        if (timeSinceLastTrack >= ONE_DAY_MS) {
            return true;
        }
        
        // Calculate hours remaining
        const hoursRemaining = Math.ceil((ONE_DAY_MS - timeSinceLastTrack) / (60 * 60 * 1000));
        // console.log('Ad impression already tracked today for ad ID:', adId, '- can track again in', hoursRemaining, 'hours');
        return false;
    }

    /**
     * Get REST API base URL (must include subpath when site is e.g. localhost/outlier-unified-backend).
     */
    function getRestUrl() {
        if (window.wpApiSettings && window.wpApiSettings.root) {
            return window.wpApiSettings.root.replace(/\/$/, '');
        }
        var path = window.location.pathname.replace(/\/$/, '');
        var segments = path ? path.split('/').filter(Boolean) : [];
        var basePath = segments.length > 1 ? '/' + segments.slice(0, -1).join('/') : '';
        return window.location.origin + basePath + '/wp-json';
    }

    /**
     * Track ad impression via REST API (POST only).
     */
    function trackAdImpression(adId) {
        if (!hasTargetingConsent()) {
            return false;
        }

        // Skip if already tracked in this session
        if (trackedAds.has(adId)) {
            return true;
        }

        // Check if ad was tracked in the last 24 hours (localStorage)
        if (!canTrackAd(adId)) {
            return true;
        }

        // Mark as tracked immediately to prevent duplicate calls
        trackedAds.add(adId);
        saveImpression(adId);

        const restUrl = getRestUrl();
        const postUrl = restUrl + '/custom/v1/ad-impression';

        // Read country from page URL query params (for localhost testing: ?geoip=1&country=GB)
        var urlParams = new URLSearchParams(window.location.search);
        var countryCode = urlParams.get('country') || null;

        var headers = { 'Content-Type': 'application/json' };
        if (window.wpApiSettings && window.wpApiSettings.nonce) {
            headers['X-WP-Nonce'] = window.wpApiSettings.nonce;
        }

        var body = { ad_id: parseInt(adId, 10), type: 'impression' };
        if (countryCode) {
            body.country_code = countryCode;
        }

        fetch(postUrl, {
            method: 'POST',
            headers: headers,
            body: JSON.stringify(body),
            credentials: 'same-origin',
            cache: 'no-store'
        }).catch(function() {});

        return true;
    }

    // Store the observer instance to prevent multiple observers
    let globalObserver = null;
    const observedElements = new WeakSet();

    /**
     * Initialize Intersection Observer for ad tracking
     */
    function initAdTracking() {
        // console.log('Initializing ad tracking...');
        
        // Find all ad items that haven't been observed yet
        const allAdElements = document.querySelectorAll('.ad-item[data-ad-id]');
        const adElements = Array.from(allAdElements).filter(function(el) {
            return !observedElements.has(el);
        });
        
        // console.log('Found ad elements:', adElements.length, '(total:', allAdElements.length, ')');
        
        if (adElements.length === 0) {
            if (allAdElements.length > 0) {
                // console.log('All ad elements already being observed');
            } else {
                // console.warn('No ad elements found with class .ad-item[data-ad-id]');
            }
            return;
        }

        // Log all found ad IDs
        adElements.forEach(function(adElement) {
            const adId = adElement.getAttribute('data-ad-id');
            // console.log('Found ad element with ID:', adId);
        });

        // Check if Intersection Observer is supported
        if (typeof IntersectionObserver === 'undefined') {
            // console.warn('IntersectionObserver not supported, tracking all ads immediately');
            // Fallback: track all ads on page load
            adElements.forEach(function(adElement) {
                const adId = adElement.getAttribute('data-ad-id');
                if (adId) {
                    // Delay slightly to ensure page is loaded
                    setTimeout(function() {
                        trackAdImpression(adId);
                    }, 500);
                }
            });
            return;
        }

        // Create or reuse Intersection Observer
        if (!globalObserver) {
            globalObserver = new IntersectionObserver(function(entries) {
                entries.forEach(function(entry) {
                    // Track when ad enters viewport (50% visible threshold)
                    if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                        const adId = entry.target.getAttribute('data-ad-id');
                        // console.log('Ad entered viewport:', adId, 'intersectionRatio:', entry.intersectionRatio);
                        if (adId) {
                            const didTrack = trackAdImpression(adId);
                            // Stop observing only after we are allowed to track.
                            if (didTrack) {
                                globalObserver.unobserve(entry.target);
                            }
                        }
                    }
                });
            }, {
                // Trigger when 50% of ad is visible
                threshold: 0.5,
                // Start tracking slightly before ad enters viewport
                rootMargin: '50px'
            });
        }

        // Observe all ad items that haven't been observed yet
        adElements.forEach(function(adElement) {
            globalObserver.observe(adElement);
            observedElements.add(adElement);
            // console.log('Observing ad element:', adElement.getAttribute('data-ad-id'));
        });

        // Also observe dynamically added ads (for AJAX-loaded content)
        if (typeof MutationObserver !== 'undefined') {
            const mutationObserver = new MutationObserver(function(mutations) {
                mutations.forEach(function(mutation) {
                    mutation.addedNodes.forEach(function(node) {
                        if (node.nodeType === 1) { // Element node
                            const adElements = node.matches && node.matches('.ad-item[data-ad-id]')
                                ? [node]
                                : node.querySelectorAll ? node.querySelectorAll('.ad-item[data-ad-id]') : [];
                            
                            adElements.forEach(function(adElement) {
                                if (!observedElements.has(adElement)) {
                                    if (!globalObserver) {
                                        // Initialize observer if it doesn't exist yet
                                        initAdTracking();
                                        return;
                                    }
                                    globalObserver.observe(adElement);
                                    observedElements.add(adElement);
                                }
                            });
                        }
                    });
                });
            });

            mutationObserver.observe(document.body, {
                childList: true,
                subtree: true
            });
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initAdTracking);
    } else {
        initAdTracking();
    }

    // Re-initialize for dynamically loaded content (e.g., AJAX pagination)
    if (typeof jQuery !== 'undefined') {
        jQuery(document).on('ajaxComplete', function() {
            setTimeout(initAdTracking, 100);
        });
    }

    document.addEventListener('OneTrustGroupsUpdated', function() {
        if (hasTargetingConsent()) {
            setTimeout(initAdTracking, 100);
        }
    });

})();
